import { z } from 'zod';
import { ORGANISATIONS, SIGN_IDS } from './signs';
import { preTriageCategory, triageCategorySchema, triageFindingsSchema } from './triage';

// Zod's built-in messages are English; the instructor never sees English (import errors,
// editor save, export all go through these schemas), so translate once here rather than at
// each call site. This file is the schema module every other schema module imports
// (directly or via events.ts/files.ts), so setting it here runs before any parse call.
z.setErrorMap((issue, ctx) => {
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      return {
        message:
          issue.received === 'undefined'
            ? 'Erforderlich'
            : `Falscher Typ (erwartet ${issue.expected})`,
      };
    case z.ZodIssueCode.too_small:
      if (issue.type === 'string') return { message: 'Darf nicht leer sein' };
      if (issue.type === 'array') return { message: `Mindestens ${issue.minimum} Einträge nötig` };
      if (issue.type === 'number') {
        return {
          message: issue.inclusive
            ? `Muss mindestens ${issue.minimum} sein`
            : `Muss größer als ${issue.minimum} sein`,
        };
      }
      return { message: ctx.defaultError };
    case z.ZodIssueCode.too_big:
      if (issue.type === 'string') return { message: `Höchstens ${issue.maximum} Zeichen` };
      if (issue.type === 'array') return { message: `Höchstens ${issue.maximum} Einträge` };
      if (issue.type === 'number') {
        return {
          message: issue.inclusive
            ? `Muss höchstens ${issue.maximum} sein`
            : `Muss kleiner als ${issue.maximum} sein`,
        };
      }
      return { message: ctx.defaultError };
    case z.ZodIssueCode.invalid_enum_value:
      return { message: `Ungültiger Wert (erlaubt: ${issue.options.join(', ')})` };
    case z.ZodIssueCode.invalid_union_discriminator:
      return { message: `Unbekannter Typ (erlaubt: ${issue.options.join(', ')})` };
    case z.ZodIssueCode.invalid_literal:
      return { message: `Ungültiger Wert (erwartet ${JSON.stringify(issue.expected)})` };
    default:
      return { message: ctx.defaultError };
  }
});

export const coordinateSchema = z.object({ x: z.number(), y: z.number() });
export type Coordinate = z.infer<typeof coordinateSchema>;

export const unitStatusSchema = z.enum(['alerted', 'onScene', 'requested', 'arrived', 'departed']);
export type UnitStatus = z.infer<typeof unitStatusSchema>;

export const scenarioModeSchema = z.enum(['orgl', 'lna', 'both']);
export type ScenarioMode = z.infer<typeof scenarioModeSchema>;

export const signTypeSchema = z.enum(SIGN_IDS);
// Optional everywhere so files written before it existed stay valid; absent means the sign's default.
export const organisationSchema = z.enum(ORGANISATIONS);

export const patientSchema = z.object({
  id: z.string().min(1),
  number: z.number().int().positive(),
  position: coordinateSchema,
  summary: z.string(),
  findings: z.array(z.object({ question: z.string(), answer: z.string() })),
  triage: triageFindingsSchema,
  expectedCategory: triageCategorySchema,
  instructorNote: z.string().optional(),
});
export type Patient = z.infer<typeof patientSchema>;

export const unitSchema = z.object({
  id: z.string().min(1),
  type: signTypeSchema,
  callsign: z.string(),
  position: coordinateSchema.optional(),
  status: unitStatusSchema,
  organisation: organisationSchema.optional(),
});
export type Unit = z.infer<typeof unitSchema>;

export const drawingSchema = z.discriminatedUnion('kind', [
  z.object({
    id: z.string().min(1),
    kind: z.literal('line'),
    points: z.array(coordinateSchema).min(2),
    color: z.string(),
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal('polygon'),
    points: z.array(coordinateSchema).min(3),
    color: z.string(),
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal('text'),
    position: coordinateSchema,
    text: z.string(),
    color: z.string(),
  }),
]);
export type Drawing = z.infer<typeof drawingSchema>;

// Inject effects are event payloads. The payload schema lives in events.ts and imports
// this file, so injects are typed loosely here and validated in files.ts (Task 4).
export const injectSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  text: z.string(),
  effects: z.array(z.unknown()).optional(),
});
export type Inject = z.infer<typeof injectSchema>;

// What the pager shows when the units are alerted. Optional so older scenarios stay valid.
export const alertSchema = z.object({
  time: z.string(), // free text, e.g. "14:32"
  address: z.string(), // the location as the dispatcher wrote it, not a coordinate
  message: z.string(), // Meldebild
  units: z.string(), // who was alerted, free text
});
export type Alert = z.infer<typeof alertSchema>;

export const scenarioSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    mode: scenarioModeSchema,
    situation: z.object({ text: z.string(), location: coordinateSchema, keyword: z.string() }),
    alert: alertSchema.optional(),
    patients: z.array(patientSchema),
    units: z.array(unitSchema),
    injects: z.array(injectSchema),
    drawings: z.array(drawingSchema),
    limitedResources: z.boolean().optional(),
    disaster: z.boolean().optional(), // Katastrophenlage; independent of limitedResources
  })
  .superRefine((scenario, ctx) => {
    // A patient's expectedCategory must be the category the triage algorithm itself derives
    // from that patient's findings, so an imported or hand-edited scenario can never show the
    // instructor a Soll-category that contradicts the reasons displayed right below it.
    const limitedResources = scenario.limitedResources ?? false;
    scenario.patients.forEach((patient, index) => {
      const derived = preTriageCategory(patient.triage, limitedResources);
      if (patient.expectedCategory !== derived) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['patients', index, 'expectedCategory'],
          message: `Patient ${patient.number}: Soll-Kategorie SK ${patient.expectedCategory} stimmt nicht mit der aus der Sichtung abgeleiteten Kategorie SK ${derived} überein.`,
        });
      }
    });
  });
export type Scenario = z.infer<typeof scenarioSchema>;

export function emptyScenario(id: string): Scenario {
  return {
    id,
    name: 'Neues Szenario',
    mode: 'both',
    situation: { text: '', location: { x: 0, y: 0 }, keyword: '' },
    patients: [],
    units: [],
    injects: [],
    drawings: [],
  };
}
